import * as echarts from 'echarts';
import { useSelector } from 'react-redux';
import { DEFAULT_THEME_MODE, ThemeMode } from '../../../theme/guideTokens';
import { ChartPalette, getChartPalette } from '../../../theme/chartPalette';

/**
 * Tema de echarts armado con la paleta de la guía (claro / oscuro / alto contraste): textos,
 * ejes, grillas, leyendas, tooltips y fondo transparente. Los gráficos pasan el nombre que
 * devuelve `useEChartsTheme()` en la prop `theme` de `ReactECharts`; al cambiar el modo cambia
 * el nombre y echarts-for-react vuelve a crear el gráfico con los colores nuevos.
 */
const buildTheme = (p: ChartPalette) => {
    const axis = {
        axisLine: { lineStyle: { color: p.axisLine } },
        axisTick: { lineStyle: { color: p.axisLine } },
        axisLabel: { color: p.textSecondary },
        nameTextStyle: { color: p.textSecondary },
        splitLine: { lineStyle: { color: [p.gridLine] } },
        splitArea: { areaStyle: { color: ['transparent'] } },
    };
    return {
        color: p.series,
        backgroundColor: 'transparent',
        textStyle: { color: p.text },
        title: { textStyle: { color: p.text }, subtextStyle: { color: p.textSecondary } },
        legend: { textStyle: { color: p.textSecondary }, pageTextStyle: { color: p.textSecondary } },
        tooltip: {
            backgroundColor: p.tooltipBackground,
            borderColor: p.axisLine,
            textStyle: { color: p.tooltipText },
            axisPointer: { lineStyle: { color: p.axisLine }, crossStyle: { color: p.axisLine } },
        },
        categoryAxis: axis,
        valueAxis: axis,
        timeAxis: axis,
        logAxis: axis,
        line: { itemStyle: { borderWidth: 2 } },
        pie: { itemStyle: { borderColor: p.surface } },
        markLine: { lineStyle: { color: p.textSecondary }, label: { color: p.textSecondary } },
        dataZoom: { textStyle: { color: p.textSecondary } },
    };
};

const registered = new Set<string>();

export const getEChartsThemeName = (mode: ThemeMode = DEFAULT_THEME_MODE): string => {
    const name = `ichtys-${mode}`;
    if (!registered.has(name)) {
        echarts.registerTheme(name, buildTheme(getChartPalette(mode)));
        registered.add(name);
    }
    return name;
};

interface ThemeSlice {
    Theme?: { mode?: ThemeMode };
}

/** Nombre del tema de echarts para el modo activo (registrado la primera vez que se usa). */
export const useEChartsTheme = (): string =>
    getEChartsThemeName(useSelector((state: ThemeSlice) => state.Theme?.mode ?? DEFAULT_THEME_MODE));

const paletteCache = new Map<ThemeMode, ChartPalette>();

/**
 * Igual que `useChartPalette()` pero devuelve siempre el mismo objeto para un modo, así se puede
 * usar como dependencia de `useMemo` sin recalcular las opciones del gráfico en cada render.
 */
export const useStableChartPalette = (): ChartPalette => {
    const mode = useSelector((state: ThemeSlice) => state.Theme?.mode ?? DEFAULT_THEME_MODE);
    let palette = paletteCache.get(mode);
    if (!palette) {
        palette = getChartPalette(mode);
        paletteCache.set(mode, palette);
    }
    return palette;
};

/** Modo de tema activo (claro / oscuro / alto contraste). */
export const useThemeMode = (): ThemeMode =>
    useSelector((state: ThemeSlice) => state.Theme?.mode ?? DEFAULT_THEME_MODE);
